import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Fragment, useState } from 'react';
import { useKandidaat } from '../api/kandidaten.ts';
import { useMatchMutaties } from '../api/matches.ts';
import type { Kandidaat, Match, Matchbeoordeling, Vacature } from '../api/typen.ts';
import { useVacature } from '../api/vacatures.ts';
import { datumTijd } from '../helpers/formatteer.ts';
import { matchbeoordelingLabels, opties } from '../helpers/labels.ts';
import { useMeldingen } from './Meldingen.tsx';
import { KandidaatVerwijzing, VacatureVerwijzing } from './Verwijzingen.tsx';
import { VoordrachtDialoog } from './VoordrachtDialoog.tsx';

interface Props {
  matches: readonly Match[];
  /** Welke kant van de match vaststaat op deze pagina: de kandidaat of de vacature. */
  vast: { kandidaat: Kandidaat } | { vacature: Vacature };
}

const beoordelingOpties = opties(matchbeoordelingLabels);

function VoordragenVanuitMatch({ match, vast, onSluit }: { match: Match; vast: Props['vast']; onSluit: () => void }) {
  const kandidaat = useKandidaat('kandidaat' in vast ? null : match.kandidaatId);
  const vacature = useVacature('vacature' in vast ? null : match.vacatureId);
  const vasteKandidaat = 'kandidaat' in vast ? vast.kandidaat : kandidaat.data;
  const vasteVacature = 'vacature' in vast ? vast.vacature : vacature.data;
  if (!vasteKandidaat || !vasteVacature) {
    return null;
  }
  return <VoordrachtDialoog open onSluit={onSluit} kandidaat={vasteKandidaat} vacature={vasteVacature} />;
}

/**
 * Een tabel met berekende matches: score, engineversie, beoordeling door de recruiter en de knop om de
 * kandidaat voor te dragen. De toelichting van de matchengine is uit te klappen.
 * @param props De matches en welke kant vaststaat.
 * @returns De tabel.
 */
export function MatchesTabel({ matches, vast }: Props) {
  const mutaties = useMatchMutaties();
  const { meld, meldFout } = useMeldingen();
  const [open, setOpen] = useState<string | null>(null);
  const [voordragen, setVoordragen] = useState<Match | null>(null);
  const toonKandidaat = 'vacature' in vast;

  const beoordeel = (match: Match, beoordeling: Matchbeoordeling) => {
    mutaties.beoordeel
      .mutateAsync({ id: match.id, beoordeling })
      .then(() => meld('Beoordeling opgeslagen'))
      .catch(meldFout);
  };

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{toonKandidaat ? 'Kandidaat' : 'Vacature'}</TableCell>
            <TableCell sx={{ width: 180 }}>Score</TableCell>
            <TableCell>Engine</TableCell>
            <TableCell>Berekend</TableCell>
            <TableCell>Beoordeling</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {matches.map((match) => (
            <Fragment key={match.id}>
              <TableRow hover>
                <TableCell>
                  {toonKandidaat ? (
                    <KandidaatVerwijzing id={match.kandidaatId} />
                  ) : (
                    <VacatureVerwijzing id={match.vacatureId} />
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <LinearProgress
                      variant="determinate"
                      value={match.score}
                      color={match.score >= 75 ? 'success' : match.score >= 50 ? 'primary' : 'warning'}
                      sx={{ flex: 1, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="body2" sx={{ minWidth: 36, textAlign: 'right' }}>
                      {match.score}%
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>{match.engineversie}</TableCell>
                <TableCell>{datumTijd(match.berekendOp)}</TableCell>
                <TableCell>
                  <Select
                    size="small"
                    value={match.beoordeling}
                    onChange={(gebeurtenis) => beoordeel(match, gebeurtenis.target.value as Matchbeoordeling)}
                    sx={{ minWidth: 150 }}
                  >
                    {beoordelingOpties.map((optie) => (
                      <MenuItem key={optie.waarde} value={optie.waarde}>
                        {optie.label}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Button size="small" onClick={() => setVoordragen(match)}>
                    Voordragen
                  </Button>
                  <IconButton
                    size="small"
                    aria-label="Toelichting"
                    onClick={() => setOpen(open === match.id ? null : match.id)}
                    sx={{ transform: open === match.id ? 'rotate(180deg)' : undefined, transition: 'transform 0.2s' }}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={6} sx={{ py: 0, borderBottom: open === match.id ? undefined : 'none' }}>
                  <Collapse in={open === match.id} unmountOnExit>
                    <Box
                      component="pre"
                      sx={{ m: 0, py: 1.5, fontSize: 12, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                    >
                      {JSON.stringify(match.toelichting, null, 2)}
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </Fragment>
          ))}
        </TableBody>
      </Table>
      {voordragen && <VoordragenVanuitMatch match={voordragen} vast={vast} onSluit={() => setVoordragen(null)} />}
    </>
  );
}
